import { useCallback } from 'react';
import { last } from 'lodash';
import useApi from '@/hooks/useApi';
import * as download from '@/requests/download';
import { useAuthentication } from '@/context/Authentication';
import { DownloadCreatePayload, DownloadType } from '@/types/download';
import { isOfType } from '@/utils/user';
import * as utils from '@/utils/misc';
import { UserType } from '@/types/user';

type Payload = Omit<DownloadCreatePayload, 'downloadType' | 'fileType'>;

enum FileType {
  Model = 'Model',
  Image = 'Image',
  Other = 'Other',
}

const extensionsByType: Record<FileType, string[]> = {
  Model: ['3ds', 'revit', 'sketchup', 'ifc', 'dwg', 'dxf', 'fbx', 'stl', 'obj'],
  Image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'webp', 'svg'],
  Other: [],
}

const getFileTypeFromUrl = (url: string) => {
  const extension = last(url.split('.'));
  if (extension) {
    for (const [type, extensions] of Object.entries(extensionsByType)) {
      if (extensions.includes(extension)) {
        return type as FileType;
      }
    }
  }
  return FileType.Other;
}

const getFileNameFromUrl = (url: string) => {
  const parts = url.split('/');
  return last(parts);
}

const useDownload = () => {
  const { user } = useAuthentication();
  const { request } = useApi(download.create);

  const track = useCallback(
    (payload: Payload) => {
      if (user) {
        const fileType = getFileTypeFromUrl(payload.url!);
        const url = utils.absoluteUrl(payload.url!);
        request({
          ...payload,
          url,
          fileType,
          downloadType: isOfType(user, UserType.PressArea)
            ? DownloadType.PressDownload
            : DownloadType.LibraryDownload,
        });
      }
    },
    [user]
  );

  return ({ url, fileName = getFileNameFromUrl(url), sku }: Payload) => {
    utils.download(url!, fileName);
    track({
      url,
      sku,
      fileName,
    });
  };
};

export default useDownload;
